/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  createContext,
  useState,
  useMemo,
  useEffect,
  ReactNode,
} from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import {
  baseDark, baseLight, errorDark,
  errorLight, greyDark, greyLight, primaryDark,
  primaryLight, successDark, successLight,
  warningDark, warningLight,
} from './colors';
import { displayTextVariants, textVariants } from './typography';
import { customShadows } from './shadows';
import { Shadows } from '@mui/material/styles/shadows';

import type { } from '@mui/lab/themeAugmentation';

declare module '@mui/material/Chip' {
  interface ChipPropsSizeOverrides {
    large: true;
    xlarge: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsSizeOverrides {
    xl: true;
    d2xl: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsSizeOverrides {
    xl: true;
    d2xl: true;
  }
}

declare module '@mui/material/IconButton' {
  interface IconButtonPropsSizeOverrides {
    xl: true;
    d2xl: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsSizeOverrides {
    xl: true;
    d2xl: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    d2xl?: true;
    dxl?: true;
    dlg?: true;
    dmd?: true;
    dsm?: true;
    dxs?: true;
    txl?: true;
    tlg?: true;
    tmd?: true;
    tsm?: true;
    txs?: true;
  }
}

declare module '@mui/material/styles' {
  interface Palette {
    base: Palette['primary'];
  }
  interface PaletteOptions {
    base: PaletteOptions['primary'];
  }
  interface PaletteColor {
    25?: string;
    50?: string;
    100?: string;
    200?: string;
    300?: string;
    400?: string;
    500?: string;
    600?: string;
    700?: string;
    800?: string;
    900?: string;
    primary?: string;
    errorPrimary?: string;
    buttonText?: string;
    foreground?: string;
    background?: string;
    bodyText?: string;
  }
  interface SimplePaletteColorOptions {
    25?: string;
    50?: string;
    100?: string;
    200?: string;
    300?: string;
    400?: string;
    500?: string;
    600?: string;
    700?: string;
    800?: string;
    900?: string;
    primary?: string;
    errorPrimary?: string;
    buttonText?: string;
    foreground?: string;
    background?: string;
  }
  interface TypographyVariants {
    d2xl: React.CSSProperties;
    dxl: React.CSSProperties;
    dlg: React.CSSProperties;
    dmd: React.CSSProperties;
    dsm: React.CSSProperties;
    dxs: React.CSSProperties;
    txl: React.CSSProperties;
    tlg: React.CSSProperties;
    tmd: React.CSSProperties;
    tsm: React.CSSProperties;
    txs: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    d2xl?: React.CSSProperties;
    dxl?: React.CSSProperties;
    dlg?: React.CSSProperties;
    dmd?: React.CSSProperties;
    dsm?: React.CSSProperties;
    dxs?: React.CSSProperties;
    txl?: React.CSSProperties;
    tlg?: React.CSSProperties;
    tmd?: React.CSSProperties;
    tsm?: React.CSSProperties;
    txs?: React.CSSProperties;
  }
}

type MainThemeProps = {
  children: ReactNode;
  forceTheme?: 'light' | 'dark';
};

export const ColorModeContext = createContext({ toggleColorMode: (newMode?: 'light' | 'dark') => {} });

export const MainTheme = ({ children, forceTheme }: MainThemeProps) => {
  const [mode, setMode] = useState<'light' | 'dark'>('light');
  const colorMode = useMemo(
    () => ({
      toggleColorMode: (newMode?: 'light' | 'dark') => {
        if (newMode) setMode(newMode);
        else setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    [],
  );

  const theme = React.useMemo(
    () => {
      let themeMode: 'light' | 'dark' = 'light';
      if (forceTheme === 'light' || forceTheme === 'dark') {
        themeMode = forceTheme;
      } else {
        themeMode = mode;
      }


      return createTheme({
        palette: {
          mode: themeMode,
          ...(themeMode === 'light'
            ? {
            // palette values for light mode
              primary: {
                main: primaryLight[600],
                ...primaryLight,
                ...baseLight,
              },
              grey: {
                ...greyLight,
              },
              error: {
                main: errorLight[400],
                ...errorLight,
              },
              warning: {
                main: warningLight[400],
                ...warningLight,
              },
              success: {
                main: successLight[400],
                ...successLight,
              },
              base: {
                main: baseLight.primary,
                ...baseLight,
              },
              text: {
                primary: '#1A324A',
              },
            }
            : {
            // palette values for dark mode
              primary: {
                main: primaryDark[200],
                ...primaryDark,
                ...baseDark,
              },
              grey: {
                ...greyDark,
              },
              error: {
                main: errorDark[400],
                ...errorDark,
              },
              warning: {
                main: warningDark[400],
                ...warningDark,
              },
              success: {
                main: successDark[400],
                ...successDark,
              },
              base: {
                main: baseDark.primary,
                ...baseDark,
              },
              text: {
                primary: '#fff',
              },
            }),
        },
        // Use name of spacing in design - formula `${4 * x}px` where x is name of spacing
        spacing: 4,
        typography: {
          fontFamily: ['Inter', 'sans-serif'].join(','),
          button: {
            textTransform: 'none',
          },
          ...displayTextVariants,
          ...textVariants,
        },
        components: {
          MuiIconButton: {
            defaultProps: {
              disableRipple: true,
            },
            styleOverrides: {
              root: {
                borderRadius: '8px',
                boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
                svg: {
                  height: '20px',
                  width: '20px',
                },
              },
            },
            variants: [
              {
                props: { size: 'small' },
                style: {
                  padding: '7px',
                },
              },
              {
                props: { size: 'medium' },
                style: {
                  padding: '8px',
                },
              },
              {
                props: { size: 'large' },
                style: {
                  padding: '10px',
                },
              },
              {
                props: { size: 'xl' },
                style: {
                  padding: '12px',
                },
              },
              {
                props: { size: 'd2xl' },
                style: {
                  padding: '16px',
                },
              },
              {
                props: { color: 'primary' },
                style: {
                  background: '#5B8AC5',
                  svg: {
                    stroke: '#fff',
                  },
                  ':hover': {
                    background: '#3F6798',
                  },
                  ':active': {
                    outline: themeMode === 'light' ? '4px solid #DAEAFF' : '4px solid #242C3A',
                  },
                },
              },
              {
                props: { color: 'secondary' },
                style: {
                  backgroundColor: themeMode === 'light' ? '#fff' : baseDark.background,
                  border: 'none',
                  outline: themeMode === 'light' ? `1px solid ${greyLight[300]}` : `1px solid ${greyDark[300]}`,
                  svg: {
                    stroke: themeMode === 'light' ? primaryLight[700] : primaryDark[700],
                  },
                  ':hover': {
                    color: themeMode === 'light' ? primaryLight[900] : primaryDark[900],
                    svg: {
                      stroke: themeMode === 'light' ? primaryLight[900] : primaryDark[900],
                    },
                  },
                  ':active': {
                    outline: themeMode === 'light' ? `4px solid ${primaryLight[200]}` : `4px solid ${primaryDark[200]}`,
                  },
                },
              },
            ],
          },
          MuiButton: {
            variants: [
              {
                props: { size: 'small' },
                style: {
                  fontSize: '14px',
                  lineHeight: '20px',
                  padding: '6px 14px',
                  svg: {
                    height: '13px',
                    width: '13px',
                  },
                },
              },
              {
                props: { size: 'medium' },
                style: {
                  fontSize: '14px',
                  lineHeight: '20px',
                  padding: '10px 18px',
                  svg: {
                    height: '16px',
                    width: '16px',
                  },
                },
              },
              {
                props: { size: 'large' },
                style: {
                  fontSize: '16px',
                  lineHeight: '24px',
                  padding: '10px 18px',
                  svg: {
                    height: '16px',
                    width: '16px',
                  },
                },
              },
              {
                props: { size: 'xl' },
                style: {
                  fontSize: '16px',
                  lineHeight: '24px',
                  padding: '12px 20px',
                  svg: {
                    height: '20px',
                    width: '20px',
                  },
                },
              },
              {
                props: { size: 'd2xl' },
                style: {
                  fontSize: '18px',
                  lineHeight: '28px',
                  padding: '16px 28px',
                  svg: {
                    height: '24px',
                    width: '24px',
                  },
                },
              },
            ],
            styleOverrides: {
              root: {
                fontWeight: '500',
                borderRadius: '8px',
                ':focus': {
                  outline: themeMode === 'light' ? `4px solid ${primaryLight[200]}` : `4px solid ${primaryDark[200]}`,
                },
                ':disabled': {
                  backgroundColor: themeMode === 'light' ? primaryLight[200] : primaryDark[25],
                  color: themeMode === 'light' ? primaryLight[25] : greyDark[200],
                },
                '.MuiButton-startIcon': {
                  marginRight: '10px',
                  marginLeft: '-2px',
                },
                '.MuiButton-endIcon': {
                  marginLeft: '10px',
                  marginRight: '-2px',
                },
              },
              outlined: {
                backgroundColor: themeMode === 'light' ? '#fff' : baseDark.background,
                color: themeMode === 'light' ? primaryLight[700] : primaryDark[700],
                border: 'none',
                outline: themeMode === 'light' ? `1px solid ${greyLight[300]}` : `1px solid ${greyDark[300]}`,
                ':hover': {
                  border: 'none',
                  color: themeMode === 'light' ? primaryLight[900] : primaryDark[900],
                },
                ':active': {
                  color: themeMode === 'light' ? primaryLight[700] : primaryDark[700],
                  outline: themeMode === 'light' ? `4px solid ${primaryLight[200]}` : `4px solid ${primaryDark[200]}`,
                },
                ':disabled': {
                  backgroundColor: themeMode === 'light' ? '#fff' : baseDark.background,
                  outline: themeMode === 'light' ? `1px solid ${greyLight[200]}` : `1px solid ${greyDark[200]}`,
                  color: themeMode === 'light' ? greyLight[700] : greyDark[700],
                  border: 'none',
                },
                '&.MuiButton-outlinedError': {
                  color: themeMode === 'light' ? errorLight[700] : errorDark[700],
                  outline: themeMode === 'light' ? `1px solid ${errorLight[200]}` : `1px solid ${errorDark[200]}`,
                  ':focus': {
                    outline: themeMode === 'light' ? `4px solid ${errorLight[300]}` : `4px solid ${errorDark[300]}`,
                  },
                  ':hover': {
                    backgroundColor: themeMode === 'light' ? errorLight[50] : errorDark[50],
                  },
                  ':disabled': {
                    backgroundColor: 'transparent',
                    color: themeMode === 'light' ? errorLight[300] : errorDark[300],
                    outline: '1px solid #FECDCA',
                  },
                },
                '&.MuiButton-outlinedSuccess': {
                  color: '#32D583',
                  outline: '1px solid #A6F4C5',
                  ':focus': {
                    outline: themeMode === 'light' ? `4px solid ${successLight[200]}` : `4px solid ${successDark[200]}`,
                  },
                  ':hover': {
                    backgroundColor: themeMode === 'light' ? successLight[50] : successDark[50],
                  },
                  ':disabled': {
                    backgroundColor: 'transparent',
                    color: '#A6F4C5',
                    outline: '1px solid #A6F4C5',
                  },
                },
                svg: {
                  stroke: themeMode === 'light' ? primaryLight[900] : primaryDark[900],
                },
              },
              contained: {
                boxShadow: 'none',
                '&.MuiButton-containedError': {
                  color: '#fff',
                  ':focus': {
                    outline: '4px solid #FECDCA',
                  },
                  ':disabled': {
                    backgroundColor: '#FECDCA',
                    color: '#fff',
                  },
                },
                '&.MuiButton-containedSuccess': {
                  color: '#fff',
                  ':focus': {
                    outline: '4px solid #D1FADF',
                  },
                  ':disabled': {
                    backgroundColor: '#D1FADF',
                    color: '#fff',
                  },
                },
                svg: {
                  stroke: '#fff',
                },
                ':hover': {
                  backgroundColor: '#3F6798',
                  boxShadow: 'none',
                },
              },
              text: {
                ':focus': {
                  outline: 'none',
                },
                '&.MuiButton-textError': {
                  color: '#B42318;',
                  ':focus': {
                    outline: 'none',
                  },
                  ':disabled': {
                    backgroundColor: 'transparent',
                    color: '#FDA29B',
                  },
                },
              },
            },
          },
          MuiButtonGroup: {
            styleOverrides: {
              grouped: {
                fontSize: '14px',
                color: themeMode === 'light' ? greyLight[700] : greyDark[700],
                '&:not(:first-of-type)': {
                  marginLeft: '1px',
                },
                ':disabled': {
                  color: themeMode === 'light' ? greyLight[200] : greyDark[200],
                },
              },
            },
          },
          MuiLoadingButton: {
            styleOverrides: {
              loadingIndicator: {
                color: '#fff',
              },
            },
          },
          MuiChip: {
            variants: [
              {
                props: {
                  size: 'large',
                },
                style: {
                  width: 'auto',
                  height: '28px',
                  ...textVariants.tsm,
                },
              },
              {
                props: {
                  size: 'xlarge',
                },
                style: {
                  width: 'auto',
                  height: '36px',
                  ...textVariants.tmd,
                },
              },
            ],
            styleOverrides: {
              sizeSmall: {
                width: 'auto',
                height: '22px',
                ...textVariants.txs,
              },
              sizeMedium: {
                width: 'auto',
                height: '24px',
                ...textVariants.tsm,
              },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                backgroundColor: themeMode === 'light' ? 'rgba(52,64,84,0.92)' : 'rgba(52,64,84,0.98)',
              },
            },
          },
        },
      });
    },
    [mode, forceTheme],
  );

  // It's the only way we can add shadows to mui theme
  theme.shadows = [...theme.shadows, ...customShadows] as Shadows;

  useEffect(() => {
    const savedMode = localStorage.getItem('colorMode');
    if (savedMode === 'light' || savedMode === 'dark') colorMode.toggleColorMode(savedMode);
  }, []);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <StyledThemeProvider theme={theme}>
        <ThemeProvider theme={theme}>
          {children}
        </ThemeProvider>
      </StyledThemeProvider>
    </ColorModeContext.Provider >
  );
};
